import { LegalPlaceholder } from "@/components/layout/LegalPlaceholder";

/**
 * /community-rules: canonical version of the Whistll community rules.
 *
 * Copy is mirrored from the legacy mobile app's CommunityRules page
 * (apps/legacy-mobile/src/pages/CommunityRules.jsx) so players see the
 * same rules on the web and in the App. When the new React Native app
 * rebuilds this screen, it should import the same 8 rule bodies.
 *
 * After editing copy here, re-run scripts/render-community-rules-pdf.mjs
 * to regenerate the PDF in ~/Documents/Whistll/whistll-all-documents/.
 */
export function CommunityRulesPage() {
  return (
    <LegalPlaceholder title="Community Rules" lastUpdated="April 2026">
      <p>
        Whistll works because people show up, play fair, and keep it real.
        These are the rules everyone agrees to when they use the app. They
        sit alongside the{" "}
        <a href="/terms" className="underline underline-offset-2">
          Terms of Service
        </a>
        . Break them enough and you'll get flagged; two flags in a rolling
        12-month period means an automatic 3-day ban, repeat offences go
        further.
      </p>

      <h2>1. Respect Every Player</h2>
      <p>
        Treat all players with respect regardless of skill level,
        background, or team. Offensive, insulting, or discriminatory
        language is not tolerated. Trash talk is part of the game but
        abuse is not. Any harassment, threatening behaviour, or
        aggression will result in an immediate ban.
      </p>

      <h2>2. Play Fair</h2>
      <p>
        Abide by the laws of the game. Deliberate fouling, ball-hogging,
        or unsportsmanlike conduct can result in removal from the match.
        Hosts have the right to remove players who spoil the experience
        for others.
      </p>

      <h2>3. Be Reliable</h2>
      <p>
        Arrive at least 10 minutes before tip-off. If you can't make it,
        withdraw as early as possible so someone else can take your spot.
        Repeated no-shows or late arrivals will lower your reliability
        score and may restrict you from future bookings.
      </p>

      <h2>4. Pay Your Share</h2>
      <p>
        When you join a paid game you commit to paying your split.
        Refunds are only issued if you withdraw before the match is
        filled and confirmed.
      </p>

      <h2>5. Respect the Space</h2>
      <p>
        Do not smoke or consume alcohol prior to or on-site at any game.
      </p>

      <h2>6. Media &amp; Highlights</h2>
      <p>
        Only upload footage you have rights to. No footage of other
        players without their implied consent. Being on the court counts
        as consent for action clips. Explicit, harmful, or misleading
        content will be removed and may result in a ban.
      </p>

      <h2>7. Disputes &amp; Reports</h2>
      <p>
        If something goes wrong, use the in-app report tools and don't
        retaliate. Admins review all reports within 48 hours. Abusing the
        reporting system is itself a violation.
      </p>

      <h2>8. Keep It Private</h2>
      <p>
        Don't share venue details, booking codes, or other players'
        personal information outside the platform. Match chat is private
        and keep it that way.
      </p>

      <h2>9. Play Safe</h2>
      <p>
        Basketball has inherent risk. You play at your own risk and
        confirm you're fit to participate. Bring your own safety
        equipment and don't play under the influence of anything that
        could impair you on the court. Whistll is a platform, not a
        coach or medic.
      </p>

      <h2>10. One Account, One Person</h2>
      <p>
        One active account per person. No duplicate accounts, no
        accounts in someone else's name, no impersonation. You must be
        18 or older to use Whistll. Keep your login credentials secure
        as you're responsible for anything that happens under your
        account.
      </p>

      <h2>Contact</h2>
      <p>
        <strong>Email:</strong>{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        <br />
        <strong>Company:</strong> Whistll Ltd, company no. 17172530,
        registered in England &amp; Wales.
        <br />
        <strong>Address:</strong> 71-75 Shelton Street, Covent Garden,
        London, WC2H 9JQ.
      </p>
    </LegalPlaceholder>
  );
}
